(() => {
  const DEFAULT_ERROR = 'Das Zubehör konnte nicht in den Warenkorb gelegt werden. Bitte versuchen Sie es erneut.';

  class ProductUpsell extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.form = document.getElementById(this.dataset.productFormId);
      if (!this.form) return;

      this.controls = Array.from(this.querySelectorAll('[data-upsell-variant-id]'));
      this.groups = Array.from(this.querySelectorAll('[data-upsell-group]'));
      this.status = this.querySelector('[data-upsell-status]');
      this.error = this.querySelector('[data-upsell-error]');
      this.productForm = this.form.closest('product-form');
      this.mainQuantityInput = this.form.elements?.namedItem?.('quantity') || null;
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.productForm?.querySelector('[type="submit"]');
      this.submitButtonAriaDisabled = this.submitButton?.getAttribute('aria-disabled');
      this.dynamicCheckout = this.productForm?.querySelector('.shopify-payment-button');
      this.dynamicCheckoutWasHidden = this.dynamicCheckout?.hidden || false;
      this.activateVariantGroup(this.dataset.selectedVariantId);
      this.basePrice = ProductUpsell.parsePrice(this.dataset.basePrice);
      this.baseCompareAtPrice = ProductUpsell.parsePrice(this.dataset.baseCompareAtPrice);
      this.currency = this.dataset.currency || 'EUR';
      this.locale = this.dataset.locale || document.documentElement.lang || 'de-DE';
      this.currencyCodeEnabled = this.dataset.currencyCodeEnabled === 'true';
      this.prepareVariantChoices();

      this.onChange = this.handleChange.bind(this);
      this.onMainQuantityChange = this.handleMainQuantityChange.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onInput = this.handleInput.bind(this);
      this.onFocusIn = this.handleFocusIn.bind(this);
      this.onDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.onDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onPickerClose = this.handlePickerClose.bind(this);
      this.picker?.addEventListener('close', this.onPickerClose);
      this.addEventListener('change', this.onChange);
      this.mainQuantityInput?.addEventListener('change', this.onMainQuantityChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.addEventListener('focusin', this.onFocusIn);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      document.addEventListener('keydown', this.onDocumentKeyDown);
      window.addEventListener('submit', this.onSubmit, true);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        if (this.dataset.quantityPricing === 'true') {
          this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.updateSelectionState(false));
        }
      }
      this.initialized = true;
      this.updateSelectionState(false);
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.removeEventListener('change', this.onChange);
      this.mainQuantityInput?.removeEventListener('change', this.onMainQuantityChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('focusin', this.onFocusIn);
      this.closeVariantPicker();
      this.picker?.removeEventListener('close', this.onPickerClose);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('keydown', this.onDocumentKeyDown);
      window.removeEventListener('submit', this.onSubmit, true);
      this.variantChangeUnsubscriber?.();
      this.quantityUpdateUnsubscriber?.();
      this.initialized = false;
    }

    handleChange(event) {
      if (event.target.matches('[data-upsell-picker-option]')) {
        if (this.inProgress || !this.pickerState) return;
        this.pickerSelection[event.target.dataset.upsellPickerOption] = event.target.value;
        this.renderPickerFields();
        return;
      }
      if (event.target.matches('[data-upsell-quantity]')) {
        ProductUpsell.normalizeQuantityInput(event.target);
        this.clearError();
        this.updateSelectionState(true);
        return;
      }
      if (!event.target.matches('[data-upsell-variant-id]')) return;
      const choiceState = this.variantStates?.get(event.target);
      if (choiceState && !this.isApprovedControl(event.target)) event.target.checked = false;
      this.syncConnectionMode();
      if (event.target.checked) this.enforceSelectionMode(event.target);
      this.clearError();
      this.updateSelectionState(true);
      if (event.target.checked) {
        this.updateMobileInfo(event.target);
      } else if (this.mobileInfo?.dataset.upsellVariantId === event.target.dataset.upsellVariantId) {
        this.updateMobileInfo(null);
      }
    }

    handleClick(event) {
      if (event.target.closest?.('[data-upsell-picker-close]') || event.target === this.picker) {
        this.closeVariantPicker();
        return;
      }
      if (this.inProgress) return;
      if (event.target.closest?.('[data-upsell-picker-apply]')) {
        this.applyPickerChoice();
        return;
      }
      const changeButton = event.target.closest?.('[data-upsell-change]');
      if (changeButton && this.contains(changeButton)) {
        const control = changeButton.closest('[data-upsell-item]')?.querySelector('[data-upsell-variant-id]');
        this.openVariantPicker(this.variantStates?.get(control), changeButton);
        return;
      }
      const pageButton = event.target.closest?.('[data-upsell-page-action]');
      if (pageButton && this.contains(pageButton) && !pageButton.disabled) {
        this.changePage(pageButton);
        return;
      }

      const toggleButton = event.target.closest?.('[data-upsell-toggle]');
      if (toggleButton && this.contains(toggleButton) && !toggleButton.disabled) {
        this.toggleUpsell(toggleButton);
        return;
      }

      const button = event.target.closest?.('[data-upsell-quantity-action]');
      if (!button) {
        const tile = event.target.closest?.('.product-upsell__tile');
        const control = tile?.closest?.('[data-upsell-item]')?.querySelector?.('[data-upsell-variant-id]');
        if (control && this.contains(control) && !control.disabled) this.updateMobileInfo(control);
        return;
      }
      if (!this.contains(button) || button.disabled) return;

      const input = button.closest('[data-upsell-selection-row]')?.querySelector('[data-upsell-quantity]');
      if (!input) return;

      const delta = button.dataset.upsellQuantityAction === 'decrease' ? -1 : 1;
      input.value = String(Math.max(1, ProductUpsell.parseQuantity(input.value) + delta));
      this.clearError();
      this.updateSelectionState(true);
    }

    prepareVariantChoices() {
      this.variantStates = new Map();
      this.picker = this.querySelector('[data-upsell-variant-dialog]');
      try {
        this.connection = JSON.parse(this.querySelector('[data-upsell-connection]')?.textContent || '{}');
      } catch { this.connection = {}; }
      this.connectionMode = 'native';
      this.querySelectorAll('[data-upsell-options]').forEach(node => {
        const item = node.closest('[data-upsell-item]');
        const control = item?.querySelector('[data-upsell-variant-id]');
        if (!control) return;
        try {
          const data = JSON.parse(node.textContent);
          const state = { item, control, data, choice: null, variants: ProductUpsell.approvedVariants(data, 'native') };
          this.variantStates.set(control, state);
          this.renderChoiceState(state);
        } catch {
          control.checked = false;
          control.disabled = true;
          item.hidden = true;
          item.dataset.upsellIneligible = 'true';
        }
      });
      this.groups.forEach(group => this.updateGroupPage(group, 0, false));
    }

    static approvedVariants(data, mode = 'native') {
      const ids = new Set(((mode === 'adapter' ? data.adapterIds : data.nativeIds) || []).map(String));
      return (data.variants || []).filter(v => ids.has(String(v.id)) && v.available === true &&
        /^\d+$/.test(String(v.id)) && Number.isFinite(v.price) && v.price >= 0 &&
        Array.isArray(v.options) && v.options.length === data.options?.length);
    }

    static optionModel(options, variants, selection = {}) {
      const varying = [], fixed = [], fields = [];
      options.forEach((name, index) => {
        const values = [...new Set(variants.map(v => v.options[index]))];
        if (values.length === 1) fixed.push({ name, value: values[0], index });
        if (values.length > 1) varying.push({ name, index });
      });
      for (const {name, index} of varying) {
        const matching = variants.filter(v => varying.every(o => o.index === index || !selection[o.index] || v.options[o.index] === selection[o.index]));
        fields.push({name, index, values: [...new Set(matching.map(v => v.options[index]))], value: selection[index] || ''});
      }
      const matches = variants.filter(v => varying.every(o => selection[o.index] && v.options[o.index] === selection[o.index]));
      return {fixed, fields, choice: matches.length === 1 ? matches[0] : null};
    }

    isApprovedControl(control) {
      const state = this.variantStates?.get(control);
      if (!state) return true; // Existing legacy, explicitly referenced single variants.
      return Boolean(state.choice && String(state.choice.id) === String(control.dataset.upsellVariantId) &&
        state.variants.some(v => String(v.id) === String(state.choice.id)));
    }

    currentDn(state) {
      return this.connectionMode === 'adapter' && state.data.adapterIds?.length ? '100' : state.data.dn;
    }

    priceLabel(variants) {
      if (!variants.length) return '';
      const prices = variants.map(v => v.price), min = Math.min(...prices);
      return (Math.max(...prices) > min ? 'ab ' : '') + this.formatMoney(min);
    }

    renderChoiceState(state) {
      const {item, control, data, variants} = state;
      if (variants.length === 1) state.choice = variants[0];
      const choice = state.choice, single = variants.length === 1;
      item.dataset.upsellIneligible = variants.length ? 'false' : 'true';
      if (!variants.length) item.hidden = true;
      const button = item.querySelector('[data-upsell-toggle]');
      if (button) {
        button.disabled = !variants.length;
        button.dataset.upsellChooseVariant = String(!choice && variants.length > 1);
        const label = button.querySelector('.product-upsell__add-label--default');
        if (label) label.textContent = choice || single ? '+ Hinzufügen' : 'Variante wählen';
      }
      const summary = item.querySelector('[data-upsell-variant-summary]');
      if (summary) summary.textContent = choice ? choice.title :
        (this.currentDn(state) ? `Passend für DN ${this.currentDn(state)} · mehrere Ausführungen` : 'Mehrere Ausführungen');
      const price = item.querySelector('.product-upsell__price');
      if (price) price.textContent = choice ? this.formatMoney(choice.price) : this.priceLabel(variants);
      const change = item.querySelector('[data-upsell-change]');
      if (change) change.hidden = variants.length <= 1 || !choice;
      control.disabled = !choice;
      control.dataset.upsellVariantId = choice ? String(choice.id) : '';
      control.value = choice ? String(choice.id) : '';
      control.dataset.upsellPrice = choice ? String(choice.price) : '0';
      control.dataset.upsellTitle = choice ? `${data.productTitle} ${choice.title === 'Default Title' ? '' : choice.title}`.trim() : data.productTitle;
      control.dataset.upsellPriceLabel = choice ? this.formatMoney(choice.price) : this.priceLabel(variants);
      const quantityTitle = item.querySelector('.product-upsell__selection-row .visually-hidden[id]');
      if (quantityTitle) quantityTitle.textContent = `Menge für ${control.dataset.upsellTitle}`;
      const quantityLabel = item.querySelector('.product-upsell__selection-row label');
      if (quantityLabel) quantityLabel.textContent = `Menge von ${control.dataset.upsellTitle}`;
      item.querySelectorAll?.('[data-upsell-quantity-action]').forEach(button => {
        button.setAttribute('aria-label', `Menge von ${control.dataset.upsellTitle} ${button.dataset.upsellQuantityAction === 'increase' ? 'erhöhen' : 'verringern'}`);
      });
      const link = item.querySelector('.product-upsell__title');
      if (link) link.href = choice?.url || data.productUrl;
      if (choice?.image) {
        const image = item.querySelector('.product-upsell__image');
        if (image?.tagName === 'IMG') {
          image.src = choice.image;
          image.removeAttribute('srcset');
          image.alt = `${data.productTitle} ${choice.title}`;
        }
      }
    }

    syncConnectionMode() {
      if (!this.variantStates || !this.connection?.adapterVariantId) return;
      const adapter = this.controls.find(c => c.dataset.upsellVariantId === this.connection.adapterVariantId &&
        !c.closest?.('[data-upsell-group]')?.hidden);
      const mode = adapter?.checked && !adapter.disabled ? 'adapter' : 'native';
      if (mode === this.connectionMode) return;
      this.closeVariantPicker();
      this.connectionMode = mode;
      for (const state of this.variantStates.values()) {
        if (!state.data.adapterIds?.length) continue;
        state.variants = ProductUpsell.approvedVariants(state.data, mode);
        if (!state.variants.some(v => String(v.id) === String(state.choice?.id))) {
          state.choice = null;
          state.control.checked = false;
        }
        this.renderChoiceState(state);
      }
      this.groups.forEach(g => this.updateGroupPage(g, 0, false));
    }

    openVariantPicker(state, trigger) {
      if (!state || !this.picker || state.variants.length <= 1 || this.inProgress) return;
      this.pickerState = state;
      this.pickerTrigger = trigger;
      this.pickerSelection = {};
      if (state.choice) state.choice.options.forEach((value, index) => { this.pickerSelection[index] = value; });
      this.picker.querySelector('[data-upsell-picker-product]').textContent = state.data.productTitle;
      this.renderPickerFields(true);
      this.picker.showModal();
      document.body.classList.add('upsell-variant-dialog-open');
      this.picker.querySelector('[data-upsell-picker-close]')?.focus();
    }

    renderPickerFields(reset = false) {
      if (!this.pickerState) return;
      const state = this.pickerState, selection = this.pickerSelection;
      let model = ProductUpsell.optionModel(state.data.options, state.variants, selection);
      // Values can only be selected if a real Shopify combination exists with the other choices.
      for (const field of model.fields) if (selection[field.index] && !field.values.includes(selection[field.index])) delete selection[field.index];
      model = ProductUpsell.optionModel(state.data.options, state.variants, selection);
      const fields = this.picker.querySelector('[data-upsell-picker-fields]');
      if (reset) fields.replaceChildren();
      for (const field of model.fields) {
        let select = fields.querySelector(`[data-upsell-picker-option="${field.index}"]`);
        if (!select) {
          const label = document.createElement('label');
          select = document.createElement('select');
          const id = `${this.picker.id || this.dataset.sectionId}-option-${field.index}`;
          label.htmlFor = id;
          label.textContent = field.name;
          select.id = id;
          select.dataset.upsellPickerOption = String(field.index);
          select.required = true;
          const row = document.createElement('div');
          row.className = 'product-upsell__variant-dialog-field';
          row.append(label, select);
          fields.append(row);
        }
        const values = ['', ...field.values];
        // Keep the native select alive while its choice commits; replacing it and
        // focusing a new select can reopen the dropdown and require a second choice.
        if (select.options.length !== values.length || values.some((value, index) => select.options[index]?.value !== value)) {
          select.replaceChildren(new Option('Bitte wählen', ''), ...field.values.map(value => new Option(value, value)));
        }
        select.value = selection[field.index] || '';
      }
      this.picker.querySelector('[data-upsell-picker-fixed]').textContent = model.fixed.map(o =>
        o.name.toLowerCase() === 'dn' ? `Passend für DN ${o.value}` : `${o.name}: ${o.value}`).join(' · ');
      const matching = state.variants.filter(v => model.fields.every(f => !selection[f.index] || v.options[f.index] === selection[f.index]));
      this.picker.querySelector('[data-upsell-picker-price]').textContent = model.choice ? this.formatMoney(model.choice.price) : this.priceLabel(matching);
      const apply = this.picker.querySelector('[data-upsell-picker-apply]');
      apply.disabled = !model.choice;
      apply.textContent = state.control.checked ? 'Übernehmen' : '+ Hinzufügen';
    }

    applyPickerChoice() {
      const state = this.pickerState;
      if (!state || this.inProgress) return;
      const model = ProductUpsell.optionModel(state.data.options, state.variants, this.pickerSelection);
      if (!model.choice) return;
      const quantity = ProductUpsell.quantityInput(state.control)?.value;
      state.choice = model.choice;
      state.control.checked = true;
      this.renderChoiceState(state);
      this.enforceSelectionMode(state.control);
      if (quantity) ProductUpsell.quantityInput(state.control).value = quantity;
      this.clearError();
      this.syncConnectionMode();
      this.updateSelectionState(true);
      this.closeVariantPicker();
    }

    closeVariantPicker() {
      if (this.picker?.open) {
        this.picker.close();
        // Return focus before the next pointer action; the native close event is queued.
        this.handlePickerClose();
      }
    }

    handlePickerClose() {
      if (!this.pickerState && !this.pickerTrigger) return;
      document.body.classList.remove('upsell-variant-dialog-open');
      const state = this.pickerState;
      this.pickerState = null;
      // The action remains on the same card even when its chosen variant changes.
      let returnTarget = state?.item.querySelector('[data-upsell-toggle]') || this.pickerTrigger;
      if (returnTarget?.closest('[data-upsell-group]')?.hidden) returnTarget = this.activeGroup?.querySelector('[data-upsell-toggle]');
      if (returnTarget?.isConnected) returnTarget.focus();
      this.pickerTrigger = null;
    }

    handleInput(event) {
      if (!event.target.matches('[data-upsell-quantity]')) return;
      if (Number.parseInt(event.target.value, 10) >= 1) this.updateSelectionState(false);
    }

    handleFocusIn(event) {
      const control = event.target.closest?.('[data-upsell-variant-id]');
      if (control && this.contains(control)) this.updateMobileInfo(control);
    }

    handleDocumentPointerDown(event) {
      if (!this.contains(event.target)) this.updateMobileInfo(null);
    }

    handleDocumentKeyDown(event) {
      if (event.key === 'Tab' && this.picker?.open) {
        const focusable = Array.from(this.picker.querySelectorAll('button:not(:disabled), select:not(:disabled)'));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (first && ((!event.shiftKey && document.activeElement === last) ||
          (event.shiftKey && document.activeElement === first) || !this.picker.contains(document.activeElement))) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      }
      if (event.key === 'Escape' && this.picker?.open) {
        event.preventDefault();
        this.closeVariantPicker();
      }
      if (event.key === 'Escape') this.updateMobileInfo(null);
    }

    toggleUpsell(button) {
      const control = button.closest?.('[data-upsell-item]')?.querySelector?.('[data-upsell-variant-id]');
      const state = this.variantStates?.get(control);
      if (state && !state.choice && state.variants.length > 1) {
        this.openVariantPicker(state, button);
        return;
      }
      if (!control || control.disabled) return;
      if (!this.isApprovedControl(control)) return;

      control.checked = !control.checked;
      if (control.checked) this.enforceSelectionMode(control);
      this.syncConnectionMode();
      this.clearError();
      this.updateSelectionState(true);
    }

    changePage(button) {
      this.closeVariantPicker();
      const group = button.closest?.('[data-upsell-group]');
      if (!group) return;

      const currentPage = Number.parseInt(group.dataset.upsellPage, 10) || 0;
      const direction = button.dataset.upsellPageAction === 'previous' ? -1 : 1;
      this.updateGroupPage(group, currentPage + direction, true);
    }

    updateGroupPage(group, requestedPage = 0, focusFirstItem = false) {
      const items = Array.from(group?.querySelectorAll?.('[data-upsell-page-item]') || []).filter(item => item.dataset?.upsellIneligible !== 'true');
      if (items.length === 0) return;

      const pageSize = Math.max(1, Number.parseInt(group.dataset.upsellPageSize, 10) || 3);
      const pageCount = Math.ceil(items.length / pageSize);
      const page = Math.min(Math.max(0, requestedPage), pageCount - 1);
      const firstIndex = page * pageSize;
      const lastIndex = Math.min(firstIndex + pageSize, items.length);

      group.dataset.upsellPage = String(page);
      items.forEach((item, index) => {
        item.hidden = index < firstIndex || index >= lastIndex;
      });

      const previous = group.querySelector?.('[data-upsell-page-action="previous"]');
      const next = group.querySelector?.('[data-upsell-page-action="next"]');
      if (previous) previous.disabled = page === 0;
      if (next) next.disabled = page >= pageCount - 1;

      const status = group.querySelector?.('[data-upsell-page-status]');
      if (status) status.textContent = `Zubehörprodukte ${firstIndex + 1} bis ${lastIndex} von ${items.length}`;

      if (focusFirstItem) {
        items[firstIndex]?.querySelector?.('[data-upsell-toggle], .product-upsell__tile')?.focus?.();
      }
    }

    handleMainQuantityChange(event) {
      if (event.target !== this.mainQuantityInput) return;

      const quantity = Number.parseInt(event.target.value, 10);
      if (!Number.isFinite(quantity) || quantity < 1) return;

      this.clearError();
      this.updateSelectionState(false);
    }

    handleVariantChange({ data } = {}) {
      if (!data?.variant || String(data.sectionId) !== String(this.dataset.sectionId)) return;

      this.basePrice = ProductUpsell.parsePrice(data.variant.price);
      this.baseCompareAtPrice = ProductUpsell.parsePrice(data.variant.compare_at_price);
      this.priceState = null;
      this.activateVariantGroup(data.variant.id);
      this.updateSelectionState(false);
    }

    activateVariantGroup(mainVariantId) {
      if (!this.groups?.length) return;
      this.closeVariantPicker();

      const target =
        this.groups.find((group) => String(group.dataset.mainVariantId) === String(mainVariantId)) ||
        this.groups.find((group) => group.dataset.mainVariantId === 'all') ||
        this.groups[0];

      this.groups.forEach((group) => {
        const active = group === target;
        group.hidden = !active;
        if (active) return;
        group.querySelectorAll?.('[data-upsell-variant-id]').forEach((control) => {
          control.checked = false;
        });
      });

      this.activeGroup = target;
      this.syncConnectionMode();
      this.mobileInfo = target?.querySelector?.('[data-upsell-mobile-info]') || null;
      this.selectionList = target?.querySelector?.('[data-upsell-selection-list]') || null;
      this.updateGroupPage(target, 0, false);
      this.groups
        .filter((group) => group !== target)
        .forEach((group) => {
          const info = group.querySelector?.('[data-upsell-mobile-info]');
          if (!info) return;
          info.hidden = true;
          info.textContent = '';
          delete info.dataset.upsellVariantId;
        });
    }

    enforceSelectionMode(changedControl) {
      const groupKey = changedControl.dataset.upsellGroupKey;
      if (!groupKey) return;

      this.controls.forEach((control) => {
        if (control === changedControl || !control.checked || control.dataset.upsellGroupKey !== groupKey) return;
        if (
          changedControl.dataset.upsellAllowMultiple !== 'true' ||
          control.dataset.upsellAllowMultiple !== 'true'
        ) {
          control.checked = false;
        }
      });
    }

    updateSelectionState(announce) {
      const selected = this.selectedControls;

      if (this.dynamicCheckout) this.dynamicCheckout.hidden = selected.length > 0 || this.dynamicCheckoutWasHidden;

      this.syncQuantityControls();
      if (selected.length === 0) this.updateMobileInfo(null);
      const displayedPrice = this.updateDisplayedPrice();
      if (this.dataset?.quantityPricing === 'true') {
        this.dataset.displayedPrice = String(displayedPrice);
        this.dispatchEvent(new CustomEvent('oe:product-price-change', {
          bubbles: true,
          detail: { amount: displayedPrice },
        }));
      }

      if (announce && this.status) {
        this.status.textContent =
          selected.length === 0
            ? 'Kein Zubehör ausgewählt. Der Basispreis wird angezeigt.'
            : `${selected.length} Zubehörartikel ausgewählt. Gesamtpreis ${this.formatMoney(displayedPrice)}.`;
      }
    }

    updateMobileInfo(control) {
      if (!this.mobileInfo) return;
      if (!control) {
        this.mobileInfo.hidden = true;
        this.mobileInfo.textContent = '';
        delete this.mobileInfo.dataset.upsellVariantId;
        return;
      }

      this.mobileInfo.textContent = `${control.dataset.upsellTitle} (+${control.dataset.upsellPriceLabel})`;
      this.mobileInfo.dataset.upsellVariantId = control.dataset.upsellVariantId;
      this.mobileInfo.hidden = false;
    }

    syncQuantityControls() {
      const visibleRowsByList = new Map();
      const selectionLists = this.groups?.length
        ? this.groups.map((group) => group.querySelector?.('[data-upsell-selection-list]')).filter(Boolean)
        : [this.selectionList].filter(Boolean);
      selectionLists.forEach((list) => visibleRowsByList.set(list, 0));
      this.controls.forEach((control) => {
        const group = control.closest?.('[data-upsell-group]');
        const enabled = control.checked && !control.disabled && (!group || !group.hidden);
        const row = ProductUpsell.selectionRow(control);
        if (row) row.hidden = !enabled;
        const list = row?.closest?.('[data-upsell-selection-list]') || this.selectionList;
        if (enabled && row && list) visibleRowsByList.set(list, (visibleRowsByList.get(list) || 0) + 1);
        const item = control.closest?.('[data-upsell-item]');
        item?.classList?.toggle('product-upsell__item--selected', enabled);
        const toggle = item?.querySelector?.('[data-upsell-toggle]');
        if (toggle) {
          toggle.setAttribute('aria-pressed', String(enabled));
          toggle.setAttribute(
            'aria-label',
            this.variantStates?.get(control)?.variants.length > 1 && !this.variantStates.get(control).choice
              ? `${this.variantStates.get(control).data.productTitle}: Variante wählen`
              : enabled
              ? `${control.dataset.upsellTitle} aus der Auswahl entfernen`
              : `${control.dataset.upsellTitle} hinzufügen, ${control.dataset.upsellPriceLabel}`
          );
        }

        const input = ProductUpsell.quantityInput(control);
        if (!input) return;

        if (!enabled) input.value = '1';
        const quantity = ProductUpsell.normalizeQuantityInput(input);
        input.disabled = !enabled;
        const picker = input.closest?.('[data-upsell-quantity-picker]');
        if (picker) picker.hidden = !enabled;
        picker?.querySelectorAll?.('[data-upsell-quantity-action]').forEach((button) => {
          button.disabled = !enabled || (button.dataset.upsellQuantityAction === 'decrease' && quantity <= 1);
        });
      });
      visibleRowsByList.forEach((visibleRows, list) => {
        list.hidden = visibleRows === 0;
      });
    }

    updateDisplayedPrice() {
      const priceContainer = this.productInfo?.querySelector(`[id="price-${this.dataset.sectionId}"]`);
      const mainQuantity = this.mainQuantity;
      const selectedPrice = this.selectedControls.reduce((total, control) => {
        const price = ProductUpsell.parsePrice(control.dataset.upsellPrice);
        const quantity = ProductUpsell.selectedQuantity(control);
        const quantityMultiplier =
          control.dataset.upsellQuantityMode === 'per_main_product_quantity' ? mainQuantity : 1;
        return total + price * quantity * quantityMultiplier;
      }, 0);
      const totalPrice = this.basePrice * mainQuantity + selectedPrice;

      if (!priceContainer || !Number.isFinite(this.basePrice)) return totalPrice;

      if (mainQuantity === 1 && selectedPrice === 0 && this.dataset.quantityPricing !== 'true') {
        this.restorePriceState(priceContainer);
        return totalPrice;
      }

      this.capturePriceState(priceContainer);
      const currentPrice = this.formatMoney(totalPrice);
      priceContainer
        .querySelectorAll('.price__regular > .price-item--regular, .price__sale > .price-item--sale.price-item--last')
        .forEach((node) => {
          node.textContent = currentPrice;
        });

      if (this.baseCompareAtPrice > this.basePrice) {
        const compareAtPrice = this.formatMoney(this.baseCompareAtPrice * mainQuantity + selectedPrice);
        priceContainer.querySelectorAll('.price__sale s.price-item--regular').forEach((node) => {
          node.textContent = compareAtPrice;
        });
      }

      return totalPrice;
    }

    capturePriceState(priceContainer) {
      if (this.priceState?.container === priceContainer) return;

      const nodes = Array.from(
        priceContainer.querySelectorAll(
          '.price__regular > .price-item--regular, .price__sale > .price-item--sale.price-item--last, .price__sale s.price-item--regular'
        )
      );
      this.priceState = {
        container: priceContainer,
        values: nodes.map((node) => ({ node, text: node.textContent })),
      };
    }

    restorePriceState(priceContainer) {
      if (this.priceState?.container !== priceContainer) return;
      this.priceState.values.forEach(({ node, text }) => {
        if (node.isConnected) node.textContent = text;
      });
      this.priceState = null;
    }

    formatMoney(cents) {
      const options = {
        style: 'currency',
        currency: this.currency,
        currencyDisplay: this.currencyCodeEnabled ? 'code' : 'symbol',
      };
      return new Intl.NumberFormat(this.locale, options).format(cents / 100);
    }

    get productInfo() {
      return this.closest('product-info');
    }

    async handleSubmit(event) {
      if (event.target !== this.form) return;
      if (this.inProgress) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const selected = this.selectedControls;
      if (selected.length === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.inProgress) return;

      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      const formData = new FormData(this.form);
      const mainVariantId = formData.get('id');
      if (!mainVariantId) {
        this.showError(DEFAULT_ERROR);
        return;
      }

      const mainQuantity = Math.max(1, Number.parseInt(formData.get('quantity'), 10) || 1);
      const groupId = ProductUpsell.createGroupId(this.dataset.sourceProductId, selected);
      const items = ProductUpsell.buildItems({
        formData,
        mainVariantId,
        mainQuantity,
        sourceProductId: this.dataset.sourceProductId,
        selected,
        groupId,
      });

      this.setLoading(true);
      this.clearError();
      this.cart?.setActiveElement(document.activeElement);

      try {
        const body = { items };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || DEFAULT_ERROR);
        }

        ProductUpsell.assertCompleteAdd(result, items);

        this.publishCartUpdate(result, mainVariantId);

        if (!this.cart) {
          window.location.assign(routes.cart_url);
          return;
        }

        const mainItem = result.items?.find((item) => String(item.id) === String(mainVariantId)) || result.items?.[0];
        this.cart.classList?.remove('is-empty');
        this.cart.renderContents({ ...result, id: mainItem?.id, key: mainItem?.key });
        if (this.status) this.status.textContent = `${items.length} Artikel wurden in den Warenkorb gelegt.`;
      } catch (error) {
        this.publishCartError(error.message, mainVariantId);
        this.showError(error.message || DEFAULT_ERROR);
      } finally {
        this.setLoading(false);
      }
    }

    publishCartUpdate(cartData, mainVariantId) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-upsell',
        productVariantId: mainVariantId,
        cartData,
      });
    }

    publishCartError(message, mainVariantId) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      publish(PUB_SUB_EVENTS.cartError, {
        source: 'product-upsell',
        productVariantId: mainVariantId,
        errors: message,
        message,
      });
    }

    setLoading(loading) {
      this.inProgress = loading;
      if (!this.submitButton) return;

      if (loading) {
        this.submitButton.setAttribute('aria-disabled', 'true');
      } else if (this.submitButtonAriaDisabled === null) {
        this.submitButton.removeAttribute('aria-disabled');
      } else {
        this.submitButton.setAttribute('aria-disabled', this.submitButtonAriaDisabled);
      }
      this.submitButton.classList.toggle('loading', loading);
      this.submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
      this.error.focus?.();
    }

    get selectedControls() {
      return this.controls.filter((control) => {
        const group = control.closest?.('[data-upsell-group]');
        return control.checked && !control.disabled && (!group || !group.hidden) && this.isApprovedControl(control);
      });
    }

    get mainQuantity() {
      return ProductUpsell.parseQuantity(this.mainQuantityInput?.value);
    }

    static buildItems({ formData, mainVariantId, mainQuantity, sourceProductId, selected, groupId }) {
      const mainProperties = ProductUpsell.formProperties(formData);
      const commonProperties = {
        _upsell_group: groupId,
        _upsell_source_product: String(sourceProductId),
        _upsell_source_variant: String(mainVariantId),
      };
      const mainItem = {
        id: Number(mainVariantId),
        quantity: mainQuantity,
        properties: { ...mainProperties, ...commonProperties },
      };
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) mainItem.selling_plan = Number(sellingPlan) || sellingPlan;

      const items = [mainItem];
      selected.forEach((control) => {
        const defaultQuantity = ProductUpsell.selectedQuantity(control);
        const quantityMode =
          control.dataset.upsellQuantityMode === 'per_main_product_quantity'
            ? 'per_main_product_quantity'
            : 'per_order';
        const quantity = quantityMode === 'per_main_product_quantity' ? defaultQuantity * mainQuantity : defaultQuantity;

        items.push({
          id: Number(control.dataset.upsellVariantId),
          quantity,
          properties: {
            ...commonProperties,
            _upsell_entry: control.dataset.upsellEntry,
            _upsell_quantity_mode: quantityMode,
          },
        });
      });

      return ProductUpsell.mergeIdenticalItems(items);
    }

    static formProperties(formData) {
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)]$/);
        if (match && value !== '') properties[match[1]] = value;
      }
      return properties;
    }

    static mergeIdenticalItems(items) {
      const merged = new Map();

      items.forEach((item) => {
        const properties = Object.entries(item.properties || {}).sort(([left], [right]) => left.localeCompare(right));
        const key = JSON.stringify([item.id, item.selling_plan || null, properties]);
        const existing = merged.get(key);
        if (existing) {
          existing.quantity += item.quantity;
        } else {
          merged.set(key, { ...item, properties: { ...item.properties } });
        }
      });

      return Array.from(merged.values());
    }

    static createGroupId(sourceProductId, selected) {
      const groupKeys = [...new Set(selected.map((control) => control.dataset.upsellGroupKey).filter(Boolean))].join('+');
      const randomPart = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return [groupKeys || sourceProductId, randomPart].join(':');
    }

    static parsePrice(value) {
      const price = Number.parseInt(value, 10);
      return Number.isFinite(price) ? price : 0;
    }

    static parseQuantity(value) {
      const quantity = Number.parseInt(value, 10);
      return Number.isFinite(quantity) && quantity >= 1 ? quantity : 1;
    }

    static quantityInput(control) {
      const quantityId = control.dataset.upsellQuantityId;
      const linkedInput = quantityId ? document.getElementById?.(quantityId) : null;
      return linkedInput || control.upsellQuantityInput ||
        control.closest?.('[data-upsell-item]')?.querySelector?.('[data-upsell-quantity]') || null;
    }

    static selectionRow(control) {
      const selectionId = control.dataset.upsellSelectionId;
      const linkedRow = selectionId ? document.getElementById?.(selectionId) : null;
      return linkedRow || control.upsellSelectionRow || null;
    }

    static selectedQuantity(control) {
      const input = ProductUpsell.quantityInput(control);
      return ProductUpsell.parseQuantity(input?.value ?? control.dataset.upsellQuantityDefault);
    }

    static normalizeQuantityInput(input) {
      const quantity = ProductUpsell.parseQuantity(input.value);
      input.value = String(quantity);
      return quantity;
    }

    static assertCompleteAdd(result, expectedItems) {
      if (!Array.isArray(result?.items)) throw new Error(DEFAULT_ERROR);

      const missingItem = expectedItems.find((expected) => {
        const added = result.items.find((item) => String(item.id ?? item.variant_id) === String(expected.id));
        return !added || Number(added.quantity) < Number(expected.quantity);
      });

      if (missingItem) throw new Error(DEFAULT_ERROR);
    }
  }

  if (!customElements.get('product-upsell')) customElements.define('product-upsell', ProductUpsell);
})();
